import { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import Navbar from "../components/layout/Navbar3";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    service: "Select a service",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const heroRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroSubRef = useRef(null);
  const formRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        heroTitleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );
      gsap.fromTo(
        heroSubRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", delay: 0.4 }
      );

      // Form reveal
      gsap.fromTo(
        formRef.current,
        { y: 60, opacity: 0, scale: 0.98 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          delay: 0.6,
        }
      );
    });

    // Mouse parallax for background blobs
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email";
    }
    if (!formData.message.trim()) errors.message = "Message is required";
    if (formData.service === "Select a service")
      errors.service = "Please select a service";

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    setFormErrors(errors);
    setSubmitError("");
    setSubmitSuccess(false);

    if (Object.keys(errors).length !== 0) return;

    setIsSubmitting(true);
    const apiUrl = `${process.env.REACT_APP_CONTACT_US_API_URL}/api/contact`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.company,
          serviceOfInterest: formData.service,
          message: formData.message,
          source: "MNR-PURE-AI",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
          service: "Select a service",
        });
      } else {
        setSubmitError(data.message || "Failed to submit lead. Please try again.");
        console.error("API Error:", data);
      }
    } catch (error) {
      setSubmitError("An unexpected network error occurred.");
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-950 min-h-screen text-white relative overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />

      {/* Background Glow Blobs with mouse parallax */}
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />

      <Navbar />

      <div className="relative z-10">
        {/* Hero Section */}
        <section ref={heroRef} className="text-center pt-28 pb-12 px-4">
          <h1 
            ref={heroTitleRef}
            className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
          >
            Get in Touch
          </h1>
          <p 
            ref={heroSubRef}
            className="mt-4 text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed opacity-0"
          >
            Ready to transform your business? <span className="font-playfair italic text-white/70">Let's connect.</span>
          </p>
        </section>

        {/* Section Divider */}
        <div className="relative py-6 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse" />
        </div>

        {/* Contact Form */}
        <section id="contact" className="pb-24 px-4">
          <div 
            ref={formRef}
            className="relative max-w-3xl mx-auto opacity-0"
          >
            {/* Glow aura behind form */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-xl opacity-40" />
            
            <div className="relative bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl shadow-black/50">
              {/* Top gradient border line */}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent rounded-t-2xl" />

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Send className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Send Us a Message</h3>
                </div>

                {submitSuccess && (
                  <div className="p-4 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl flex items-center gap-3 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    Thank you! We'll get back to you shortly.
                  </div>
                )}

                {submitError && (
                  <div className="p-4 bg-red-500/10 text-red-400 border border-red-500/20 rounded-xl flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    {submitError}
                  </div>
                )}

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name <span className="text-blue-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white placeholder-gray-600 ${
                      formErrors.name ? "border-red-500/50" : "border-white/10 hover:border-white/20"
                    }`}
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {formErrors.name && (
                    <p className="text-red-400 text-sm mt-2">{formErrors.name}</p>
                  )}
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address <span className="text-blue-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john@company.com"
                      className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white placeholder-gray-600 ${
                        formErrors.email ? "border-red-500/50" : "border-white/10 hover:border-white/20"
                      }`}
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {formErrors.email && (
                      <p className="text-red-400 text-sm mt-2">{formErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white placeholder-gray-600 hover:border-white/20"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Company & Service */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Acme Inc."
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white placeholder-gray-600 hover:border-white/20"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Service of Interest <span className="text-blue-400">*</span>
                    </label>
                    <select
                      name="service"
                      className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white appearance-none ${
                        formErrors.service ? "border-red-500/50" : "border-white/10 hover:border-white/20"
                      }`}
                      value={formData.service}
                      onChange={handleChange}
                    >
                      <option value="Select a service" disabled className="bg-slate-900">Select a service</option>
                      <option className="bg-slate-900">Healthcare Solutions</option>
                      <option className="bg-slate-900">Banking Solutions</option>
                      <option className="bg-slate-900">Product Development</option>
                      <option className="bg-slate-900">AI Solutions & Consulting</option>
                      <option className="bg-slate-900">Digital Transformation</option>
                      <option className="bg-slate-900">Cloud Solutions</option>
                      <option className="bg-slate-900">R&D Solutions</option>
                      <option className="bg-slate-900">Training Programs</option>
                      <option className="bg-slate-900">Other</option>
                    </select>
                    {formErrors.service && (
                      <p className="text-red-400 text-sm mt-2">{formErrors.service}</p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Your Message <span className="text-blue-400">*</span>
                  </label>
                  <textarea
                    rows="5"
                    name="message"
                    placeholder="Tell us about your project..."
                    className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 text-white placeholder-gray-600 resize-none ${
                      formErrors.message ? "border-red-500/50" : "border-white/10 hover:border-white/20"
                    }`}
                    value={formData.message}
                    onChange={handleChange}
                  />
                  {formErrors.message && (
                    <p className="text-red-400 text-sm mt-2">{formErrors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white py-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm tracking-wide shadow-lg shadow-blue-900/30 hover:shadow-blue-800/40 hover:scale-[1.02] mt-4"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;